import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-37811e52"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "panel" }

import { computed, ref, inject, watch, PropType } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import { DxToolbarItem } from "devextreme-vue/popup";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import NumberDropdown from "@/components/ui/dropdowns/number-dropdown.vue";
import { MarketingEmailItems } from "acquaint-api/models/MarketingEmailItems";
import { EmailItems } from "acquaint-api/models/EmailItems";
import { MarketingEmailItemType } from "@/enums/emails/MarketingEmailItemType";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import { getErrorMessage } from "bright-logic-core/src/errors";
import MarketingEmailItemsConfigurationModal from "@/components/email-marketing/marketing-email-items-configuration-modal.vue";
import AcquaintInfoBox from "@/components/ui/inputs/acquaint-info-box.vue";
import { EmailItem } from "@/types/emails/EmailItem";


export default /*#__PURE__*/_defineComponent({
  __name: 'mailshot-item-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },

  /**
   * The unique identifier for the email.
   * Used to fetch or reference a specific email item.
   * @type {number}
   * @required
   */
  emailId: {
    type: Number,
    required: true,
  },

  /**
   * The selected email item data.
   * Contains details about the email currently being interacted with.
   * @type {EmailItem}
   * @required
   */
  selectedEmailItem: {
    type: Object as PropType<EmailItem>,
    required: true,
  },

  /**
   * Represents an action key related to the email item.
   * Used for tracking specific actions performed on the email.
   * @type {string}
   * @default ''
   */
  actionKey: {
    type: String,
    default: "",
  },

  /**
   * The type of marketing email item.
   * Used to determine the category or nature of the email.
   * @type {MarketingEmailItemType}
   * @required
   * @default undefined
   */
  itemType: {
    type: Number as PropType<MarketingEmailItemType>,
    default: undefined,
  },

  /**
   * The total number of menu items associated with the email.
   * Helps in managing the available actions or links in the email.
   * @type {number}
   * @required
   */
  totalMenuItems: {
    type: Number,
    required: true,
  },

  /**
   * The total number of adverts included in the email.
   * Used to keep track of marketing content within the email.
   * @type {number}
   * @required
   */
  totalAdverts: {
    type: Number,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Emitted to notify the parent component that data needs to be refreshed.
   * Typically triggered after updates were made.
   */
  "refreshEmailItemsData",
],
  setup(__props, { emit }) {

const props = __props;





const maxOrderNumber = ref(0);
const selectedType = ref(0);
const showModal = computed({
  get: () => props.modelValue,
  set: (value: Boolean) => emit("update:modelValue", value),
});
const isNewItem = computed(() => props.actionKey.includes("new"));
const dropdownIcon = computed(() => {
  return props.actionKey.includes("new")
    ? "fa-sharp fa-solid fa-caret-down"
    : "";
});
const selectedItemDescription = ref("");

const openMarketingEmailItemsModal = ref(false);
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const acquaintApiNextClient = inject(
  "AcquaintApiNextClient",
) as AcquaintApiNextClient;
const marketingEmailItemData = ref<MarketingEmailItems>();
const marketingEmailItems = ref<IDDescriptionPair[]>([]);
const emailItemData = ref<EmailItems>({
  emailID: 0,
  marketingEmailItemID: 0,
  displayOrder: 0,
});

const lockEmailItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/emailitems/${props.emailId}/${props.selectedEmailItem?.marketingEmailItemId}/lock`,
      { lock: true, extraInfo: "" },
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const unlockEmailItem = async () => {
  try {
    await acquaintApiClient.httpPostVoid(
      `database/emailitems/${props.emailId}/${props.selectedEmailItem?.marketingEmailItemId}/lock`,
      { lock: false, extraInfo: "" },
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const fetchEmailItems = async () => {
  try {
    if (isNewItem.value) {
      emailItemData.value = await acquaintApiClient.httpGet<EmailItems>(
        `database/emailItems/defaultmodel`,
      );
      emailItemData.value.displayOrder = maxOrderNumber.value;
    } else {
      emailItemData.value = await acquaintApiClient.httpGet<EmailItems>(
        `database/emailItems/${props.emailId}/${props.selectedEmailItem?.marketingEmailItemId}`,
      );
    }
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

const fetchMarketingIDDescriptionPairs = async () => {
  try {
    marketingEmailItems.value = await acquaintApiNextClient.httpGet<
      IDDescriptionPair[]
    >(`MarketingEmailItems/${props.itemType}`);
  } catch (err) {
    marketingEmailItemData.value = undefined;
    displayErrorMessage(getErrorMessage(err));
  }
};

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      return;
    }

    if (props.itemType != undefined) {
      selectedType.value = props.itemType;
    }
    await loadData();
  },
);

watch(selectedType, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    loadData();
  }
});

const loadData = async () => {
  if (selectedType.value == MarketingEmailItemType.MenuItem) {
    maxOrderNumber.value = props.totalMenuItems ? props.totalMenuItems : 0;
  } else {
    maxOrderNumber.value = props.totalAdverts ? props.totalAdverts : 0;
  }
  if (isNewItem.value) {
    selectedItemDescription.value = "";
    maxOrderNumber.value = maxOrderNumber.value ? maxOrderNumber.value + 1 : 1;
  } else {
    selectedItemDescription.value = props.selectedEmailItem?.description
      ? props.selectedEmailItem?.description
      : "";
  }
  await fetchEmailItems();
  await fetchMarketingIDDescriptionPairs();
};

const cancelClick = async () => {
  await unlockEmailItem();
  showModal.value = false;
};

const modalClosed = async () => {
  await unlockEmailItem();
};

const saveDetails = async () => {
  if (await callSaveApi()) {
    await unlockEmailItem();
    showModal.value = false;
  }
};

const callSaveApi = async () => {
  if (props.actionKey.includes("new")) {
    return await callCreateEmailItemApi();
  } else if (props.actionKey.includes("edit")) {
    await callEditEmailItemApi();
    await unlockEmailItem();
    return true;
  }
  return false;
};

const callCreateEmailItemApi = async () => {
  try {
    emailItemData.value.emailID = props.emailId;
    emailItemData.value = await acquaintApiClient.httpPost<EmailItems>(
      `database/emailItems`,
      emailItemData.value,
    );
    emit("refreshEmailItemsData");
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const callEditEmailItemApi = async () => {
  try {
    if (!(await lockEmailItem())) {
      return false;
    }

    emailItemData.value = await acquaintApiClient.httpPut<EmailItems>(
      `database/emailItems/${props.emailId}/${props.selectedEmailItem?.marketingEmailItemId}`,
      emailItemData.value,
    );
    emit("refreshEmailItemsData");
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const onConfigurationSelected = (
  marketingItemId: number,
  description: string,
) => {
  selectedItemDescription.value = description;
  emailItemData.value.emailID = props.emailId;
  emailItemData.value.displayOrder = maxOrderNumber.value;
  emailItemData.value.marketingEmailItemID = marketingItemId;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      title: "Mailshot Item",
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
      showCloseButton: true,
      height: "350px",
      onModalClosed: modalClosed,
      width: _unref(ModalWidth).Small
    }, {
      content: _withCtx(() => [
        (emailItemData.value)
          ? (_openBlock(), _createBlock(_unref(DxTabPanel), {
              key: 0,
              "scroll-by-content": true,
              "show-nav-buttons": true,
              "swipe-enabled": false
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(DxItem), { title: "General Details" }, {
                  default: _withCtx(() => [
                    _createElementVNode("section", _hoisted_1, [
                      _createVNode(AcquaintInfoBox, {
                        text: selectedItemDescription.value,
                        label: "Item",
                        icon: dropdownIcon.value,
                        onIconClicked: _cache[0] || (_cache[0] = 
                () => {
                  openMarketingEmailItemsModal.value = true;
                }
              )
                      }, null, 8, ["text", "icon"]),
                      _createVNode(NumberDropdown, {
                        label: "Order",
                        itemNumber: maxOrderNumber.value,
                        "allow-zero-value": false,
                        modelValue: emailItemData.value.displayOrder,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((emailItemData.value.displayOrder) = $event))
                      }, null, 8, ["itemNumber", "modelValue"])
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Save',
          onClick: () => {
            saveDetails();
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "width"]),
    _createVNode(MarketingEmailItemsConfigurationModal, {
      modelValue: openMarketingEmailItemsModal.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((openMarketingEmailItemsModal).value = $event)),
      "item-type": __props.itemType,
      "show-select-button": true,
      onConfigurationSelected: onConfigurationSelected
    }, null, 8, ["modelValue", "item-type"])
  ], 64))
}
}

})